import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Home } from "lucide-react";
import ComplianceFooter from "@/components/ComplianceFooter";

const TermsOfUse = () => {
  useEffect(() => {
    document.title = "Terms of Use - Ascendry";
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
          >
            <Home className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
        <h1 className="text-4xl font-bold mb-8 text-center">Terms of Use</h1>
        
        <div className="prose prose-lg max-w-none space-y-6">
          <p className="text-muted-foreground">
            <strong>Effective Date:</strong> {new Date().toLocaleDateString()}
          </p>

          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
            <p>
              By accessing and using Ascendry, you accept and agree to be bound by these Terms of Use. 
              If you do not agree to these terms, please do not use our platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
            <p>
              Ascendry is a platform that connects students with expert mentors for personalized learning 
              sessions. Our services include:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Mentor discovery and booking system</li>
              <li>Credit-based payment system</li>
              <li>Flexible session scheduling with mentor-chosen platforms</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. User Responsibilities</h2>
            <p>As a user of Ascendry, you agree to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide accurate and up-to-date information</li>
              <li>Maintain the confidentiality of your account credentials</li>
              <li>Use the platform in compliance with applicable laws</li>
              <li>Respect other users and maintain professional conduct</li>
              <li>Not misuse or abuse the platform's features</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. Payment Processing and Credit System</h2>
            
            <div className="bg-muted p-4 rounded-lg mb-6">
              <h3 className="font-semibold mb-2">Payment Processing Disclosure</h3>
              <p className="text-sm mb-2">
                All payments are processed securely through Stripe, Inc., a PCI DSS Level 1 certified payment processor. 
                Ascendry does not store your payment card information.
              </p>
              <ul className="text-sm space-y-1">
                <li><strong>Payment Security:</strong> All transactions use industry-standard encryption</li>
                <li><strong>Data Handling:</strong> Payment data is handled according to PCI DSS standards</li>
                <li><strong>Fraud Protection:</strong> Advanced fraud detection and prevention measures</li>
              </ul>
            </div>

            <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
              <h3 className="text-xl font-semibold mb-3 text-red-800 dark:text-red-200">🚨 All Sales Final Policy</h3>
              <p className="text-red-700 dark:text-red-300 font-medium mb-2">
                All token purchases are final. No refunds will be provided except as required by law or in cases of verified platform error.
              </p>
            </div>

            <h3 className="text-xl font-semibold mb-3">Credit System Terms</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Credits must be purchased before booking sessions</li>
              <li><strong>Credits expire after 1 year from purchase date</strong> - Use your tokens within 12 months</li>
              <li>Credits are non-refundable - all sales are final except as required by law or in cases of platform error</li>
              <li>Credit prices and policies may change with 30 days notice</li>
              <li>Unused credits cannot be transferred between accounts</li>
              <li>A 17.5% total fee (10% processing + 7.5% platform fee) is added to all credit purchases</li>
              <li>Bulk discount pricing is available on credit packages of 500+ credits (up to 7% discount on large bundles)</li>
              <li>Expired credits will be automatically removed from your account balance</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-6">Billing and Fees</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Transaction Fees:</strong> 17.5% total fee (10% processing + 7.5% platform fee) applied to all purchases</li>
              <li><strong>Fee Breakdown:</strong> The 10% processing fee covers payment processing, fraud prevention, and tax compliance. The 7.5% platform fee supports platform operations and mentor support.</li>
              <li><strong>Currency:</strong> All prices displayed in USD unless otherwise specified</li>
              <li><strong>Taxes:</strong> Applicable taxes are included in the processing fee</li>
              <li><strong>Billing Disputes:</strong> Must be reported within 60 days of the transaction</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-6">Credit Redemption and Mentor Payments</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Credit Value:</strong> Credits are redeemed at a standard rate of $1 per credit when booking sessions</li>
              <li><strong>Session Pricing:</strong> A session with a $100/hour mentor costs 100 credits per hour, regardless of the price you paid for credits</li>
              <li><strong>Mentor Compensation:</strong> Mentors receive 92.5% of their listed hourly rate. The platform retains a 7.5% fee to support operations</li>
              <li><strong>Discount Benefit:</strong> When you purchase discounted credit bundles, you benefit from paying less per credit while mentors still receive their full contracted rate</li>
              <li><strong>Example:</strong> If you buy 1,000 credits at $930 (7% discount), you pay $0.93 per credit plus fees. When booking a $100/hour session, 100 credits are deducted, and the mentor receives $92.50</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-6">Scheduling and Rescheduling Policy</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>First reschedule is free:</strong> You can reschedule any session once at no cost</li>
              <li><strong>Additional reschedules cost 1 token:</strong> After your first reschedule, each additional reschedule costs 1 token</li>
              <li><strong>24-hour restriction:</strong> Sessions cannot be rescheduled within 24 hours of the scheduled time</li>
              <li><strong>Cancellation within 24 hours:</strong> Sessions cancelled within 24 hours result in forfeited tokens (no refund)</li>
              <li>Session cancellations by mentors (more than 24 hours notice) result in automatic credit refunds</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-6">Refund and Chargeback Policy</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>All sales are final:</strong> Token purchases are non-refundable except as required by law</li>
              <li>Refunds are only provided for platform technical errors or unauthorized transactions</li>
              <li>Chargebacks may result in account suspension pending investigation</li>
              <li>Refund requests must be submitted within 14 days of purchase and will be evaluated on a case-by-case basis</li>
              <li>Compassionate refunds may be considered at management discretion for exceptional circumstances</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Educational Services Disclaimer</h2>
            
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 mb-6">
              <h3 className="font-bold text-amber-800 dark:text-amber-200 mb-3">🎓 EDUCATIONAL PLATFORM ONLY</h3>
              <p className="text-amber-700 dark:text-amber-300 font-medium mb-4">
                Ascendry is exclusively an educational mentorship platform. We provide legitimate learning and skill development services only.
              </p>
              
              <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">What We ARE:</h4>
              <ul className="list-disc pl-6 space-y-1 text-amber-700 dark:text-amber-300 mb-4">
                <li>Educational mentorship platform</li>
                <li>Skill development and knowledge transfer service</li>
                <li>Professional guidance and coaching</li>
                <li>Learning acceleration through expert instruction</li>
              </ul>
              
              <h4 className="font-semibold text-red-800 dark:text-red-200 mb-2">What We Are NOT:</h4>
              <ul className="list-disc pl-6 space-y-1 text-red-700 dark:text-red-300">
                <li><strong>Pay-to-play service:</strong> We do not offer guaranteed placements, opportunities, or industry connections in exchange for payment</li>
                <li><strong>Collaboration marketplace:</strong> Sessions are for learning, not arranging paid collaborations</li>
                <li><strong>Ghost production service:</strong> We do not facilitate or support ghost production arrangements</li>
                <li><strong>Payola or promotional service:</strong> We prohibit any form of payment for guaranteed exposure, playlist placements, or promotional activities</li>
                <li><strong>Networking-for-hire service:</strong> While networking may naturally occur, we do not guarantee introductions or industry connections</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold mb-3">Mentor Services</h3>
            <p className="mb-4">
              Mentors are independent contractors who provide educational services only. Ascendry facilitates connections but does not 
              guarantee the quality, outcome, or results of mentoring sessions.
            </p>
            
            <h3 className="text-xl font-semibold mb-3">Prohibited Activities</h3>
            <p className="mb-2">The following activities are strictly prohibited on our platform:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Arranging or facilitating pay-to-play schemes</li>
              <li>Promising guaranteed industry placements or opportunities</li>
              <li>Offering or requesting ghost production services</li>
              <li>Facilitating payola or paid promotional arrangements</li>
              <li>Selling access to industry contacts or exclusive opportunities</li>
              <li>Any form of quid pro quo arrangements beyond legitimate educational mentoring</li>
            </ul>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">Educational Outcomes</h3>
            <p>
              While our mentors are experts in their fields and provide high-quality educational content, we make no guarantees about:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Career advancement or success</li>
              <li>Industry opportunities or placements</li>
              <li>Financial outcomes or earning potential</li>
              <li>Skill acquisition timelines or proficiency levels</li>
              <li>Creative or professional breakthroughs</li>
            </ul>
            
            <p>
              Success in any field depends on numerous factors including individual effort, talent, market conditions, and opportunities beyond our control.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Session Platforms</h2>
            <p>
              Mentors choose their preferred communication platform (Zoom, Google Meet, Discord, etc.) 
              for conducting sessions. You are responsible for ensuring you have access to and comply 
              with the terms of service of your chosen platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Intellectual Property; Enforcement</h2>
            <p className="mb-4">
              Ascendry and all related content, code, designs, graphics, trademarks, trade dress, logos, data, 
              documentation, and other materials (the "Platform IP") are and shall remain the exclusive property 
              of Blaqout Music, LLC. You may not copy, reproduce, modify, distribute, reverse-engineer, imitate, 
              clone, create derivative works from, or otherwise use the Platform IP except as expressly authorized 
              in writing by Blaqout Music, LLC.
            </p>
            <p className="mb-4">
              Any unauthorized use of the Platform IP constitutes a breach of these Terms and may result in legal action. 
              Blaqout Music, LLC reserves the right to pursue all available remedies, including injunctive relief, 
              monetary damages, disgorgement of profits, and recovery of attorneys' fees.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-6">Copyright & IP Policy (DMCA)</h3>
            <p className="mb-4">
              If you believe that content on the Ascendry platform infringes your intellectual property rights, 
              please notify us using the procedure below. We will investigate all valid claims and take appropriate 
              action in accordance with the Digital Millennium Copyright Act (DMCA) and other applicable laws.
            </p>

            <h4 className="font-semibold mb-2">DMCA Reporting Procedure</h4>
            <p className="mb-2">To file a copyright infringement claim, please provide the following information to our designated agent:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Your physical or electronic signature</li>
              <li>Identification of the copyrighted work claimed to have been infringed</li>
              <li>Identification of the material that is claimed to be infringing and information reasonably sufficient to permit us to locate the material</li>
              <li>Your contact information (address, telephone number, and email address)</li>
              <li>A statement that you have a good faith belief that use of the material is not authorized by the copyright owner, its agent, or the law</li>
              <li>A statement, under penalty of perjury, that the information in the notification is accurate and that you are authorized to act on behalf of the copyright owner</li>
            </ul>

            <div className="bg-muted p-4 rounded-lg mb-4">
              <h4 className="font-semibold mb-2">Send DMCA Notices To:</h4>
              <p className="text-sm">
                <strong>Email:</strong>{" "}
                <a href="mailto:lance@blaqout.us" className="text-primary hover:underline">
                  lance@blaqout.us
                </a>
              </p>
              <p className="text-sm mt-2">
                <strong>Subject Line:</strong> "DMCA Copyright Infringement Notice"
              </p>
            </div>

            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> Filing a false or fraudulent DMCA claim may result in liability for damages, 
              including costs and attorneys' fees. If you are unsure whether the material infringes your rights, 
              we recommend consulting with an attorney before filing a claim.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Limitation of Liability</h2>
            <p>
              Ascendry provides the platform "as is" and disclaims all warranties. We are not liable 
              for any indirect, incidental, or consequential damages arising from your use of the platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">9. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your account if you violate these terms. 
              You may also terminate your account at any time.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">10. Data Processing and Privacy</h2>
            <p>
              Your use of our platform is also governed by our{" "}
              <Link to="/privacy-policy" className="text-primary hover:underline">
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link to="/cookie-policy" className="text-primary hover:underline">
                Cookie Policy
              </Link>. 
              By using our services, you consent to our data processing practices as described in these policies.
            </p>
            
            <div className="bg-primary/10 p-4 rounded-lg mt-4">
              <h3 className="font-semibold mb-2">GDPR Compliance Notice</h3>
              <p className="text-sm">
                If you are located in the European Union, you have additional rights under the General Data 
                Protection Regulation (GDPR). Please review our Privacy Policy for information about your rights 
                and how to exercise them.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">11. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms of Use at any time. Material changes will be 
              communicated via email or prominent notice on our platform at least 30 days before taking effect. 
              Continued use of our services after changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">12. Contact Information</h2>
            <p>
              For questions about these Terms of Use, contact us at{" "}
              <a href="mailto:lance@blaqout.us" className="text-primary hover:underline">
                lance@blaqout.us
              </a>
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <ComplianceFooter />
    </div>
  );
};

export default TermsOfUse;